export function createVideoSlider(section) {
  const controller = new AbortController();
  let mediaController;
  let swiperInstance;
  let legacyBreakpointCleanup;
  let observer;
  let disposed = false;
  let started = false;
  const timeouts = new Set();
  const intervals = new Set();
  const listen = (target, name, callback, options = {}) => target.addEventListener(name, callback, { ...options, signal: controller.signal });
  const listenMedia = (target, name, callback, options = {}) => target.addEventListener(name, callback, { ...options, signal: mediaController.signal });
  const schedule = (callback, delay) => {
    const timer = setTimeout(() => {
      timeouts.delete(timer);
      if (!disposed) callback();
    }, delay);
    timeouts.add(timer);
    return timer;
  };
  const repeat = (callback, delay) => {
    const timer = setInterval(callback, delay);
    intervals.add(timer);
    return timer;
  };
  const cancelRepeat = (timer) => { clearInterval(timer); intervals.delete(timer); };

  function start() {
    if (disposed || started) return;
    started = true;
    const autoSlide = section.dataset.autoSlide === 'true';
    const slideTimer = parseInt(section.dataset.slideTimer, 10) || 5000;
    const mobileMediaQuery = window.matchMedia('(max-width: 1024px)');
    const mediaTemplate = section.querySelector('[data-video-slider-media-template]');
    const mobileMediaTarget = section.querySelector('[data-video-slider-target="mobile"]');
    const desktopMediaTarget = section.querySelector('[data-video-slider-target="desktop"]');
    let activeMediaTarget = null;

    function updateButton(playing, playPauseBtn, playIcon, pauseIcon) {
      if (playing) {
        if (playIcon) playIcon.style.display = 'none';
        if (pauseIcon) pauseIcon.style.display = 'block';
        playPauseBtn.setAttribute('aria-label', 'Pause video');
      } else {
        if (playIcon) playIcon.style.display = 'block';
        if (pauseIcon) pauseIcon.style.display = 'none';
        playPauseBtn.setAttribute('aria-label', 'Play video');
      }
    }

    function bindVideoControls(root) {
      const wantsAutoplay = section.dataset.videoAutoplay === 'true';
      const shouldMute = section.dataset.videoMuted === 'true';

      const tryProgrammaticAutoplay = (video) => {
        if (!wantsAutoplay || !video) return;
        video.muted = shouldMute;
        video.defaultMuted = shouldMute;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        const run = () => {
          const playPromise = video.play();
          if (playPromise !== undefined && typeof playPromise.then === 'function') {
            playPromise.catch(() => {});
          }
        };
        if (video.readyState >= 2) {
          run();
        } else {
          listenMedia(video, 'loadeddata', run, { once: true });
          listenMedia(video, 'canplay', run, { once: true });
        }
      };

      const videoContainers = root.querySelectorAll('.video-slider__video-container');
      videoContainers.forEach((videoContainer) => {
        const video = videoContainer.querySelector('video');
        const playPauseBtn = videoContainer.querySelector('.video-slider__play-pause-btn');

        if (video && playPauseBtn) {
          const playIcon = playPauseBtn.querySelector('.video-slider__play-icon');
          const pauseIcon = playPauseBtn.querySelector('.video-slider__pause-icon');

          const syncButtonState = () => {
            updateButton(!video.paused && !video.ended, playPauseBtn, playIcon, pauseIcon);
          };

          tryProgrammaticAutoplay(video);

          syncButtonState();

          if (video.readyState < 2) {
            listenMedia(video, 'loadeddata', syncButtonState, { once: true });
          }

          listenMedia(playPauseBtn, 'click', async (event) => {
            event.preventDefault();
            event.stopPropagation();

            try {
              if (video.paused) {
                await video.play();
                updateButton(true, playPauseBtn, playIcon, pauseIcon);
              } else {
                video.pause();
                updateButton(false, playPauseBtn, playIcon, pauseIcon);
              }
            } catch (error) {
              console.warn('Video toggle failed', error);
              updateButton(!video.paused, playPauseBtn, playIcon, pauseIcon);
            }
          });

          listenMedia(video, 'play', () => updateButton(true, playPauseBtn, playIcon, pauseIcon));
          listenMedia(video, 'playing', () => updateButton(true, playPauseBtn, playIcon, pauseIcon));
          listenMedia(video, 'pause', () => updateButton(false, playPauseBtn, playIcon, pauseIcon));
          listenMedia(video, 'ended', () => updateButton(false, playPauseBtn, playIcon, pauseIcon));
        }
      });
    }

    function bindExternalTargets(root) {
      const externalVideoTargets = root.querySelectorAll('[data-video-external-target]');
      if (externalVideoTargets.length === 0) return;

      const autoplay = section.dataset.videoAutoplay === 'true' ? 1 : 0;
      const muted = section.dataset.videoMuted === 'true' ? 1 : 0;

      externalVideoTargets.forEach((target) => {
        const provider = target.dataset.videoProvider;
        const id = target.dataset.videoId;
        let iframeSrc = '';

        if (provider === 'youtube' && id) {
          iframeSrc = `https://www.youtube.com/embed/${id}?controls=0&showinfo=0&rel=0&autoplay=${autoplay}&mute=${muted}&loop=1&playlist=${id}&modestbranding=1&playsinline=1`;
        } else if (provider === 'vimeo' && id) {
          iframeSrc = `https://player.vimeo.com/video/${id}?background=1&loop=1&autoplay=${autoplay}&muted=${muted}&byline=0&portrait=0&title=0`;
        }

        if (iframeSrc) {
          const iframe = document.createElement('iframe');
          iframe.setAttribute('src', iframeSrc);
          iframe.setAttribute('frameborder', '0');
          iframe.setAttribute('allowfullscreen', '1');
          iframe.setAttribute(
            'allow',
            'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
          );
          iframe.setAttribute('title', 'Video Player');
          iframe.classList.add('video-slider__video-iframe--loaded');
          target.appendChild(iframe);
        }
      });
    }

    function mountSectionMedia() {
      if (!mediaTemplate || !mobileMediaTarget || !desktopMediaTarget) return;

      const nextTarget = mobileMediaQuery.matches ? mobileMediaTarget : desktopMediaTarget;
      if (activeMediaTarget === nextTarget && nextTarget.childElementCount > 0) return;

      mediaController?.abort();
      mediaController = new AbortController();
      section.querySelectorAll('video').forEach((video) => video.pause());
      mobileMediaTarget.innerHTML = '';
      desktopMediaTarget.innerHTML = '';
      nextTarget.appendChild(mediaTemplate.content.cloneNode(true));
      activeMediaTarget = nextTarget;

      bindVideoControls(nextTarget);
      bindExternalTargets(nextTarget);
    }

    mountSectionMedia();

    if ('addEventListener' in mobileMediaQuery) {
      listen(mobileMediaQuery, 'change', mountSectionMedia);
    } else if ('addListener' in mobileMediaQuery) {
      mobileMediaQuery.addListener(mountSectionMedia);
        legacyBreakpointCleanup = () => mobileMediaQuery.removeListener(mountSectionMedia);
    }

    const sliderElement = section.querySelector('[data-slider-id]');
    if (sliderElement) {
      function equalizeSlideHeights() {
        const slides = sliderElement.querySelectorAll('.video-slider__slide');
        if (slides.length === 0) return;

        slides.forEach((slide) => {
          slide.style.height = 'auto';
        });

        let maxHeight = 0;
        slides.forEach((slide) => {
          const height = slide.offsetHeight;
          if (height > maxHeight) maxHeight = height;
        });

        if (maxHeight > 0) {
          slides.forEach((slide) => {
            slide.style.height = `${maxHeight}px`;
          });
        }
      }

      function initSwiperWithModules(SwiperCtor, Pagination, Autoplay) {
        if (disposed) return;
        const swiperConfig = {
          slidesPerView: 1,
          spaceBetween: 10,
          loop: false,
          rewind: true,
          observer: true,
          observeParents: true,
          pagination: {
            el: sliderElement.querySelector('.video-slider__slider-pagination'),
            clickable: true,
            type: 'bullets'
          }
        };

        if (Pagination) swiperConfig.modules = [Pagination];

        if (autoSlide) {
          swiperConfig.autoplay = {
            delay: slideTimer,
            disableOnInteraction: false,
            pauseOnMouseEnter: true
          };

          if (Autoplay) {
            if (swiperConfig.modules) {
              swiperConfig.modules.push(Autoplay);
            } else {
              swiperConfig.modules = [Autoplay];
            }
          }
        }

        swiperInstance = new SwiperCtor(sliderElement, swiperConfig);

        if (autoSlide && swiperInstance.autoplay && typeof swiperInstance.autoplay.start === 'function') {
          swiperInstance.autoplay.start();
        }

        const restartAutoplay = () => {
          if (autoSlide && swiperInstance.autoplay && typeof swiperInstance.autoplay.start === 'function') {
            swiperInstance.autoplay.start();
          }
        };

        listen(sliderElement, 'touchend', restartAutoplay);
        listen(sliderElement, 'pointerup', restartAutoplay);

        if (autoSlide && !swiperInstance.autoplay) {
          let fallbackAutoplayTimer;

          const startFallbackAutoplay = () => {
            cancelRepeat(fallbackAutoplayTimer);
            fallbackAutoplayTimer = repeat(() => {
              if (document.hidden) return;
              swiperInstance.slideNext();
            }, slideTimer);
          };

          startFallbackAutoplay();
          listen(sliderElement, 'touchend', startFallbackAutoplay);
          listen(sliderElement, 'pointerup', startFallbackAutoplay);
          listen(document, 'visibilitychange', startFallbackAutoplay);
        }

        schedule(equalizeSlideHeights, 200);

        let resizeTimeout;
        listen(window, 'resize', () => {
          clearTimeout(resizeTimeout);
          resizeTimeout = schedule(equalizeSlideHeights, 250);
        });
      }

      function initSwiper() {
        if (disposed) return;
        if (typeof Swiper === 'undefined') {
          if (window.flu && window.flu.chunks && window.flu.chunks.swiper) {
            import(window.flu.chunks.swiper)
              .then((swiperModule) => {
                const SwiperCtor = swiperModule.Swiper || swiperModule.default;
                const { Pagination, Autoplay } = swiperModule;
                initSwiperWithModules(SwiperCtor, Pagination, Autoplay);
              })
              .catch(() => {
                console.warn('Swiper not available');
              });
          }
          return;
        }

        try {
          const Pagination = Swiper.Pagination || (window.Swiper && window.Swiper.Pagination);
          const Autoplay = Swiper.Autoplay || (window.Swiper && window.Swiper.Autoplay);
          const initExistingSwiper = () => initSwiperWithModules(Swiper, Pagination, Autoplay);

          initExistingSwiper();
        } catch (error) {
          swiperInstance = new Swiper(sliderElement, {
            slidesPerView: 1,
            spaceBetween: 10,
            loop: false,
            pagination: {
              el: sliderElement.querySelector('.video-slider__slider-pagination'),
              clickable: true,
              type: 'bullets'
            }
          });

          schedule(equalizeSlideHeights, 200);
        }
      }

      schedule(initSwiper, 100);
    }

  }

  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver((entries) => {
      if (disposed || !entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      start();
    }, {
      rootMargin: document.body.classList.contains('template-index') && !window.Shopify?.designMode ? '800px 0px' : '120px 0px',
      threshold: 0
    });
    observer.observe(section);
  } else start();

  return () => {
    if (disposed) return;
    disposed = true;
    observer?.disconnect();
    controller.abort();
    mediaController?.abort();
    legacyBreakpointCleanup?.();
    timeouts.forEach(clearTimeout);
    intervals.forEach(clearInterval);
    swiperInstance?.destroy(true, true);
    section.querySelectorAll('video').forEach((video) => video.pause());
    section.querySelectorAll('[data-video-slider-target]').forEach((target) => target.replaceChildren());
  };
}

import(window.flu.chunks.befloSectionLifecycle).then(({ mountSections }) => {
  mountSections('[data-section-type="video-slider"]', createVideoSlider);
});
